import type { ColorAttribute } from './helpers';
import type { VariantMappingRepair } from './variantMappingRepair';
import { findOptionIgnoringCase, findOptionsByLabelIgnoringCase, findValueIgnoringCase } from './variantMappingRepairStrategies/casing';
import { getReplacementAttribute, replacementKey } from './variantMappingRepairStrategies/manual';
import { repairShopifyOptionPositions } from './variantMappingRepairStrategies/shopify';
import type { buildValueReplacementPlan, ValueReplacementConflict } from './variantMappingValuePlan';
import type { AttributeReplacement, RepairContext, StoreOption } from './variantMappingRepairStrategies/types';

export type ReplacementCandidate = {
    key: string;
    attribute: ColorAttribute;
    count: number;
    options: readonly StoreOption[];
    replacement: ColorAttribute | null;
    isSelected: boolean;
    conflict?: ValueReplacementConflict;
};

/** Value choices are needed when positional/casing repairs alone cannot reconcile the original value. */
export const getEditableAttributes = (attributes: readonly ColorAttribute[], context: RepairContext, conflictingValues: ReadonlySet<string>) => {
    const retained = context.isShopify ? repairShopifyOptionPositions(attributes, context.options) : attributes;
    return attributes.filter((attribute) => {
        // Positional repairs preserve labels; values belonging to safely removed options need no replacement.
        if (retained && !retained.some((option) => option.Label === attribute.Label)) return false;
        if (conflictingValues.has(replacementKey(attribute.Id, attribute.Value.Id))) return true;
        const option = findOptionIgnoringCase(attribute, context.options, !context.isShopify);
        return !option || !findValueIgnoringCase(attribute.Value, option.values);
    });
};

/** Group the planner's editable values and attach their current choices, suggestions and validation. */
export const buildReplacementCandidates = (
    repairs: readonly VariantMappingRepair[],
    context: RepairContext,
    valuePlan: ReturnType<typeof buildValueReplacementPlan>,
    replacements: readonly AttributeReplacement[]
): ReplacementCandidate[] => {
    const groups = repairs.flatMap((repair) =>
        Array.from(new Map(repair.editableAttributes.map((attribute) => [replacementKey(attribute.Id, attribute.Value.Id), attribute])).entries()).map(
            ([key, attribute]) => ({ key, attribute, variantGuid: repair.variantGuid })
        )
    );
    return Array.from(new Map(groups.map(({ key, attribute }) => [key, attribute])).entries()).map(([key, attribute]) => {
        const matches = findOptionsByLabelIgnoringCase(attribute.Label, context.options);
        const options = matches.length === 1 ? matches : context.options;
        const selected = replacements.find((replacement) => replacementKey(replacement.optionID, replacement.valueID) === key);
        return {
            key,
            attribute,
            count: new Set(groups.filter((group) => group.key === key).map((group) => group.variantGuid)).size,
            options,
            replacement: selected ? getReplacementAttribute(attribute, selected, options) : (valuePlan.suggestions.get(key) ?? null),
            isSelected: !!selected,
            conflict: valuePlan.conflictDetails.get(key)
        };
    });
};
